import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Clock, MapPin, ArrowRight, Loader2, CheckCircle, Briefcase, FolderKanban, Search, X, Map as MapIcon, QrCode, Monitor, Flame, PhoneCall, Wallet } from 'lucide-react';
import axios from 'axios';
import ToolboxAckGate from '../components/ToolboxAckGate';
import OfflineSyncBanner from '../components/OfflineSyncBanner';
import NotificationBell from '../components/NotificationBell';
import { enqueuePunch, isNetworkError } from '../utils/offlineQueue';
import BreakTracker from '../components/timesheet/BreakTracker';
import NeedsReview from '../components/timesheet/NeedsReview';
import { QrPunchButton } from '../components/timesheet/QrPunch';
import QrScanDialog from '../components/QrScanDialog';
import MealBreakDialog from '../components/MealBreakDialog';
import ClockOutReceipt from '../components/ClockOutReceipt';

const API = `${window.__BACKEND_URL__}/api`;
const LAST_PUNCH_KEY = 'ht_last_punch_selection';
const LAST_PROJECT_KEY = 'ht_last_punch_project';

export default function ClockInScreen() {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clockingIn, setClockingIn] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [pendingToolbox, setPendingToolbox] = useState(null);
  const [toolboxChecked, setToolboxChecked] = useState(false);
  const [jobCounts, setJobCounts] = useState(null);
  // Area-aware clock-in (Feb 2026). Aug 2026 redesign: area + project
  // selection now live INLINE on this screen — no modals. One selection,
  // one Start Shift button.
  const [areas, setAreas] = useState([]);
  const [areasLoaded, setAreasLoaded] = useState(false);
  // selection: { type: 'none'|'area'|'job'|'project', label, areaLabel?, jobId?, projectId? }
  const [selection, setSelection] = useState({ type: 'none', label: null });
  // Project is REQUIRED on every punch (Sep 2026): { id, number, name }.
  const [project, setProject] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LAST_PROJECT_KEY) || 'null'); } catch { return null; }
  });
  const [attribError, setAttribError] = useState(null);
  const [userTouched, setUserTouched] = useState(false);
  const [showQrScanner, setShowQrScanner] = useState(false);
  const [showKioskInfo, setShowKioskInfo] = useState(false);
  const [scanStreak, setScanStreak] = useState(0);
  const [qrMealPrompt, setQrMealPrompt] = useState(null); // { shift_hours, tok, location }
  const [clockOutReceipt, setClockOutReceipt] = useState(null);
  const [recentShifts, setRecentShifts] = useState([]);
  const [callbackShift, setCallbackShift] = useState(false);
  const [calloutPolicy, setCalloutPolicy] = useState(null);
  const [payday, setPayday] = useState(null);
  const [paydayOpen, setPaydayOpen] = useState(false);
  const [badge, setBadge] = useState(null); // { qr_png, name } when shown
  const [badgeLoading, setBadgeLoading] = useState(false);
  // null = no block; { type: 'kiosk_only'|'qr_required', locationLabel }
  const [clockInBlocked, setClockInBlocked] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Site QR deep-link (Aug 2026): camera apps open /clock-in?qr=<token>.
  // Only consume the token once authenticated — otherwise the signin
  // redirect would clear it before the worker ever logs in.
  const [pendingQr, setPendingQr] = useState(null);
  const [qrJob, setQrJob] = useState(null); // { job_title, job_number } | { invalid: true }
  useEffect(() => {
    if (!token) return;
    try {
      const fromUrl = new URLSearchParams(window.location.search).get('qr');
      const stored = sessionStorage.getItem('ht_pending_qr');
      const tok = fromUrl || stored;
      if (tok) {
        setPendingQr(tok);
        sessionStorage.removeItem('ht_pending_qr');
      }
    } catch { /* noop */ }
  }, [token]);

  // Resolve the scanned token to its job so the worker sees WHERE they
  // are clocking in (and gets a clear message if the poster is stale).
  useEffect(() => {
    if (!pendingQr || !token) return;
    axios
      .get(`${API}/clock-in/resolve-qr`, {
        params: { token: pendingQr },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setQrJob(r.data))
      .catch(() => setQrJob({ invalid: true }));
  }, [pendingQr, token]);

  useEffect(() => {
    if (token) {
      checkStatus();
      checkToolbox();
      fetchJobCounts();
      fetchAreas();
      axios.get(`${API}/my/scan-streak`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setScanStreak(r.data?.streak_days || 0))
        .catch(() => {});
      // After-hours call-out: pre-tick when the worker is punching in inside the company window.
      axios.get(`${API}/callout/policy`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => { setCalloutPolicy(r.data); if (r.data?.in_window_now) setCallbackShift(true); })
        .catch(() => {});
      axios.get(`${API}/my/recent-shifts?limit=5`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setRecentShifts(r.data?.rows || []))
        .catch(() => {});
      axios.get(`${API}/my/payday-preview`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setPayday(r.data))
        .catch(() => {});
      // Refresh areas with GPS once so the server-side "suggested" flag
      // reflects where the worker is standing right now (non-blocking).
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => fetchAreas({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
          () => {},
          { timeout: 4000, enableHighAccuracy: true },
        );
      }
    }
  }, [token]); // eslint-disable-line

  const fetchAreas = async (coords = null) => {
    try {
      const params = {};
      if (coords && coords.latitude != null && coords.longitude != null) {
        params.lat = coords.latitude;
        params.lng = coords.longitude;
      }
      const res = await axios.get(`${API}/clock-in/areas`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setAreas(Array.isArray(res.data?.areas) ? res.data.areas : []);
    } catch {
      setAreas([]);
    } finally {
      setAreasLoaded(true);
    }
  };

  // Apple-simple default: preselect the GPS-suggested area so the common
  // case is a single tap on Start Shift. If GPS can't suggest one, fall
  // back to whatever this worker picked last time on this device.
  useEffect(() => {
    if (userTouched || !areasLoaded) return;
    const suggested = areas.find((a) => a?.suggested);
    if (suggested?.name) {
      setSelection({ type: 'area', label: suggested.name, areaLabel: suggested.name });
      return;
    }
    try {
      const saved = JSON.parse(localStorage.getItem(LAST_PUNCH_KEY) || 'null');
      if (!saved?.type) return;
      if (saved.type === 'area' && areas.some((a) => a?.name === saved.areaLabel)) {
        setSelection(saved);
      } else if (saved.type === 'job' && saved.jobId) {
        setSelection(saved);
      }
    } catch { }
  }, [areas, areasLoaded, userTouched]);

  const fetchJobCounts = async () => {
    try {
      const res = await axios.get(`${API}/jobs/my-pending-count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setJobCounts(res.data);
    } catch {
      // Non-fatal — greeting just won't show the count
    }
  };

  const checkStatus = async () => {
    try {
      const res = await axios.get(`${API}/clock-in/status`, { headers: { Authorization: `Bearer ${token}` } });
      setStatus(res.data);
    } catch { }
    finally { setLoading(false); }
  };

  const checkToolbox = async () => {
    try {
      const res = await axios.get(`${API}/toolbox/active`, { headers: { Authorization: `Bearer ${token}` } });
      const topic = res.data?.topic;
      if (topic && topic.id && res.data?.user_acknowledged !== true) {
        setPendingToolbox(topic);
      } else {
        setPendingToolbox(null);
      }
    } catch {
      setPendingToolbox(null);
    } finally {
      setToolboxChecked(true);
    }
  };

  const handleClockIn = async (opts = {}) => {
    // opts: { projectId?, areaLabel?, jobId? } — unchanged logic
    setClockingIn(true);
    let location = null;
    try {
      try {
        const pos = await new Promise((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 })
        );
        location = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
      } catch { }

      const body = { location };
      body.project_id = opts.projectId || project?.id || null;
      if (opts.areaLabel) body.area_label = opts.areaLabel;
      if (opts.jobId) body.job_id = opts.jobId;
      if (opts.siteQrToken) body.site_qr_token = opts.siteQrToken;
      if (callbackShift) body.callback_shift = true;
      setAttribError(null);
      await axios.post(`${API}/clock-in/quick`, body, { headers: { Authorization: `Bearer ${token}` } });
      goToDashboard();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 400 && detail?.code) {
        setAttribError(detail.message || 'Pick a project before starting your shift.');
        return;
      }
      if (err.response?.status === 403 && detail?.code) {
        setClockInBlocked({
          type: detail.code,
          locationLabel: detail.location_label || null,
          message: detail.message || null,
          distance: detail.distance_m ?? null,
          radius: detail.radius_m ?? null,
          jobTitle: detail.job_title || null,
        });
        return;
      }
      if (isNetworkError(err)) {
        enqueuePunch('clock_in', {
          project_id: opts.projectId || project?.id || null,
          area_label: opts.areaLabel || null,
          job_id: opts.jobId || null,
          site_qr_token: opts.siteQrToken || null,
          location,
        });
        alert("No signal — your clock-in was saved on this device with the current time and will sync automatically once you're back online.");
        goToDashboard();
      } else {
        console.error('Clock-in error:', err);
      }
    } finally {
      setClockingIn(false);
    }
  };

  const handleQrClockOut = async (tokOverride = null, deductBreak = false, confirmNoBreak = false) => {
    const tok = tokOverride || pendingQr;
    setClockingIn(true);
    let location = qrMealPrompt?.location || null;
    try {
      if (!location) {
        try {
          const pos = await new Promise((resolve, reject) =>
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 })
          );
          location = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
        } catch { }
      }
      const res = await axios.post(
        `${API}/clock-out/quick`,
        { site_qr_token: tok, location, deduct_break: deductBreak, confirm_no_break: confirmNoBreak },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Alberta ESA: server holds a 5h+ clock-out until the break question
      // is answered — never treat that response as a completed clock-out.
      if (res.data?.meal_break_warning) {
        setQrMealPrompt({ shift_hours: res.data.shift_hours, tok, location });
        return;
      }
      setQrMealPrompt(null);
      setPendingQr(null);
      setQrJob(null);
      setClockOutReceipt({ hours: res.data.hours_worked, deducted: res.data.meal_break_deducted });
      checkStatus();
    } catch (err) {
      if (isNetworkError(err)) {
        enqueuePunch('clock_out', { site_qr_token: tok, location });
        alert("No signal — your clock-out was saved on this device with the current time and will sync automatically once you're back online.");
        setPendingQr(null);
        setQrJob(null);
        setQrMealPrompt(null);
      } else {
        console.error('QR clock-out error:', err);
      }
    } finally {
      setClockingIn(false);
    }
  };

  const startShift = () => {
    if (clockingIn) return;
    if (!project?.id) { setAttribError('Pick a project before starting your shift (General Work for non-project time).'); return; }
    try { localStorage.setItem(LAST_PUNCH_KEY, JSON.stringify(selection)); } catch { }
    if (selection.type === 'area') handleClockIn({ areaLabel: selection.areaLabel });
    else if (selection.type === 'job') handleClockIn({ jobId: selection.jobId });
    else handleClockIn({});
  };

  // Picking a job that belongs to another project switches the project too.
  const pickSelection = (sel) => {
    setUserTouched(true);
    setAttribError(null);
    if (sel.type === 'job' && sel.jobProjectId && sel.jobProjectId !== project?.id) {
      pickProject({ id: sel.jobProjectId, number: sel.jobProjectNumber, name: sel.jobProjectName });
    }
    setSelection(sel);
  };

  const pickProject = (p) => {
    setAttribError(null);
    setProject(p);
    try { localStorage.setItem(LAST_PROJECT_KEY, JSON.stringify(p)); } catch { }
    if (selection.type === 'job' && p?.id !== project?.id) setSelection({ type: 'none', label: null });
  };

  const goToDashboard = () => {
    const role = user?.role;
    if (role === 'admin') navigate('/dashboard/admin');
    else if (role === 'supervisor') navigate('/dashboard/supervisor');
    else navigate('/dashboard/staff');
  };

  // Not signed in (e.g. a worker's camera app opened a poster deep-link):
  // go to signin BEFORE the data-loading gate below — those flags only
  // resolve when a token exists, so checking them first spun forever.
  const needsSignin = !authLoading && (!token || !user);
  useEffect(() => {
    if (needsSignin) navigate('/signin');
  }, [needsSignin, navigate]);

  if (needsSignin) return null;

  if (authLoading || loading || !toolboxChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B1321' }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#1E88E5' }} />
      </div>
    );
  }

  if (pendingToolbox) {
    return (
      <ToolboxAckGate
        token={token}
        topic={pendingToolbox}
        onAcknowledged={() => setPendingToolbox(null)}
      />
    );
  }

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const isAlreadyClockedIn = status?.clocked_in;

  return (
    <div className="min-h-screen flex flex-col items-center py-8 px-5" style={{ background: '#0B1321' }}>
      <div className="fixed top-0 left-0 right-0 z-50"><OfflineSyncBanner /></div>
      <div className="fixed top-3 right-3 z-40" data-testid="clock-in-bell-wrap"><NotificationBell /></div>
      <div className="w-full max-w-sm text-center my-auto">
        {/* Logo */}
        <div className="w-12 h-12 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: '#1E88E5' }}>
          <span className="text-white font-extrabold text-lg">H</span>
        </div>

        {/* Greeting */}
        <h1 className="text-2xl font-bold text-white mb-0.5" data-testid="clock-in-greeting">
          {getGreeting()}, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-xs mb-1" style={{ color: '#A0AEC0' }}>
          {currentTime.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })}
        </p>

        <p className="text-3xl font-bold text-white mb-5 font-mono" data-testid="clock-in-time">
          {currentTime.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </p>

        {/* QR scan streak badge */}
        {scanStreak >= 2 && (
          <div
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full mb-4 text-xs font-bold"
            style={{ background: 'rgba(255,160,0,0.12)', border: '1px solid rgba(255,160,0,0.45)', color: '#FFB84D' }}
            data-testid="scan-streak-badge"
          >
            <Flame className="w-3.5 h-3.5" />
            {scanStreak}-day scan streak
          </div>
        )}

        {/* Site QR deep-link banner — the poster's QR opened this page */}
        {pendingQr && !isAlreadyClockedIn && !clockInBlocked && (
          qrJob?.invalid ? (
            <div className="rounded-2xl p-4 mb-5 text-left" style={{ background: 'rgba(240,160,32,0.1)', border: '1px solid rgba(240,160,32,0.4)' }} data-testid="qr-deeplink-invalid-banner">
              <p className="text-sm font-semibold text-white mb-1">This QR poster is out of date</p>
              <p className="text-xs mb-3" style={{ color: '#A0AEC0' }}>
                The code you scanned isn't valid anymore — ask your supervisor for the latest poster. You can still clock in normally below.
              </p>
              <button
                onClick={() => { setPendingQr(null); setQrJob(null); }}
                className="px-4 py-2.5 rounded-xl text-sm font-semibold"
                style={{ color: '#A0AEC0', border: '1px solid #2D3748' }}
                data-testid="qr-deeplink-invalid-dismiss-btn"
              >
                Dismiss
              </button>
            </div>
          ) : (
            <div className="rounded-2xl p-4 mb-5 text-left" style={{ background: 'rgba(0,200,150,0.1)', border: '1px solid rgba(0,200,150,0.4)' }} data-testid="qr-deeplink-banner">
              <p className="text-sm font-semibold text-white mb-1">Site QR scanned</p>
              {qrJob?.job_title ? (
                <p className="text-sm font-medium mb-1 flex items-center gap-1.5" style={{ color: '#6EE7B7' }} data-testid="qr-deeplink-job">
                  <Briefcase className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">
                    {qrJob.job_number ? `${qrJob.job_number} — ` : ''}{qrJob.job_title}
                  </span>
                </p>
              ) : null}
              <p className="text-xs mb-3" style={{ color: '#A0AEC0' }}>
                Clock in here and the job is verified automatically.
                {qrJob?.location_locked ? ' This site checks your GPS — you must be at the location.' : ''}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => { const tok = pendingQr; setPendingQr(null); handleClockIn({ siteQrToken: tok }); }}
                  className="flex-1 py-3 rounded-xl text-sm font-bold text-white"
                  style={{ background: '#00C896' }}
                  data-testid="qr-deeplink-clockin-btn"
                >
                  Clock In at This Site
                </button>
                <button
                  onClick={() => { setPendingQr(null); setQrJob(null); }}
                  className="px-4 py-3 rounded-xl text-sm font-semibold"
                  style={{ color: '#A0AEC0', border: '1px solid #2D3748' }}
                  data-testid="qr-deeplink-dismiss-btn"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )
        )}

        {/* Site QR deep-link while ON shift — same poster clocks you out */}
        {pendingQr && isAlreadyClockedIn && !qrJob?.invalid && (
          <div className="rounded-2xl p-4 mb-5 text-left" style={{ background: 'rgba(255,160,0,0.1)', border: '1px solid rgba(255,160,0,0.4)' }} data-testid="qr-deeplink-clockout-banner">
            <p className="text-sm font-semibold text-white mb-1">Site QR scanned — end of shift?</p>
            {qrJob?.job_title ? (
              <p className="text-sm font-medium mb-1 flex items-center gap-1.5" style={{ color: '#FCD34D' }} data-testid="qr-deeplink-clockout-job">
                <Briefcase className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">
                  {qrJob.job_number ? `${qrJob.job_number} — ` : ''}{qrJob.job_title}
                </span>
              </p>
            ) : null}
            <p className="text-xs mb-3" style={{ color: '#A0AEC0' }}>
              You're currently clocked in. Scan-out is recorded as site-verified.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => handleQrClockOut()}
                disabled={clockingIn}
                className="flex-1 py-3 rounded-xl text-sm font-bold text-white disabled:opacity-50"
                style={{ background: '#F59E0B' }}
                data-testid="qr-deeplink-clockout-btn"
              >
                {clockingIn ? 'Clocking out…' : 'Clock Out at This Site'}
              </button>
              <button
                onClick={() => { setPendingQr(null); setQrJob(null); }}
                className="px-4 py-3 rounded-xl text-sm font-semibold"
                style={{ color: '#A0AEC0', border: '1px solid #2D3748' }}
                data-testid="qr-deeplink-clockout-dismiss-btn"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {clockInBlocked ? (
          <div className="flex flex-col items-center text-center px-2 py-8" data-testid="clock-in-blocked-screen">
            {/* ── LOCATION-LOCKED QR ── */}
            {(clockInBlocked.type === 'qr_out_of_range' || clockInBlocked.type === 'qr_location_required') && (
              <>
                <div
                  className="w-20 h-20 rounded-2xl flex items-center justify-center mb-5"
                  style={{ background: 'rgba(255,160,0,0.15)', border: '1px solid rgba(255,160,0,0.3)' }}
                >
                  <MapPin className="w-10 h-10" style={{ color: '#FFB84D' }} />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">
                  {clockInBlocked.type === 'qr_location_required' ? 'Location Needed' : 'Not at the Job Site'}
                </h2>
                <p className="text-sm mb-2 max-w-xs leading-relaxed" style={{ color: '#94A3B8' }} data-testid="qr-location-blocked-msg">
                  {clockInBlocked.message || 'This QR code is location-locked and can only be scanned at the job site.'}
                </p>
                {clockInBlocked.type === 'qr_out_of_range' && clockInBlocked.distance != null && (
                  <p className="text-base font-semibold text-white mb-6">
                    You're <span style={{ color: '#FFB84D' }}>{clockInBlocked.distance >= 1000 ? `${(clockInBlocked.distance / 1000).toFixed(1)} km` : `${clockInBlocked.distance} m`}</span> away
                    {clockInBlocked.radius ? <> — get within <span style={{ color: '#FFB84D' }}>{clockInBlocked.radius} m</span> and scan again.</> : '.'}
                  </p>
                )}
                {clockInBlocked.type === 'qr_location_required' && (
                  <p className="text-xs mb-6 max-w-xs" style={{ color: '#64748B' }}>
                    Allow location access in your browser or phone settings, then scan the poster again.
                  </p>
                )}
              </>
            )}

            {/* ── KIOSK ONLY ── */}
            {clockInBlocked.type === 'kiosk_only' && (
              <>
                <div
                  className="w-20 h-20 rounded-2xl flex items-center justify-center mb-5"
                  style={{ background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.3)' }}
                >
                  <Monitor className="w-10 h-10" style={{ color: '#A78BFA' }} />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">Kiosk Clock-In Required</h2>
                <p className="text-sm mb-2 max-w-xs leading-relaxed" style={{ color: '#94A3B8' }}>
                  This site only allows clock-in at the kiosk station.
                </p>
                <p className="text-base font-semibold text-white mb-6">
                  Go to{' '}
                  <span style={{ color: '#A78BFA' }}>
                    {clockInBlocked.locationLabel || 'the site kiosk'}
                  </span>
                  {' '}to clock in.
                </p>
                <div
                  className="w-full max-w-xs rounded-2xl p-4 mb-6 text-left space-y-3"
                  style={{ background: '#1A2433', border: '1px solid #2D3748' }}
                >
                  {[
                    'Find the kiosk tablet at your site entrance',
                    'Tap your name on the screen',
                    'Enter your 4-digit PIN to clock in',
                  ].map((step, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span
                        className="w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ background: 'rgba(168,85,247,0.2)', color: '#A78BFA' }}
                      >
                        {i + 1}
                      </span>
                      <p className="text-sm" style={{ color: '#CBD5E1' }}>{step}</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs mb-6" style={{ color: '#475569' }}>
                  No PIN yet? Set one from your profile menu, or ask your supervisor.
                </p>
              </>
            )}

            {/* ── QR REQUIRED ── */}
            {clockInBlocked.type === 'qr_required' && (
              <>
                <div
                  className="w-20 h-20 rounded-2xl flex items-center justify-center mb-5"
                  style={{ background: 'rgba(96,165,250,0.15)', border: '1px solid rgba(96,165,250,0.3)' }}
                >
                  <QrCode className="w-10 h-10" style={{ color: '#60A5FA' }} />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">QR Scan Required</h2>
                <p className="text-sm mb-2 max-w-xs leading-relaxed" style={{ color: '#94A3B8' }}>
                  This site requires you to scan the QR code to verify you're on site.
                </p>
                <p className="text-base font-semibold text-white mb-6">
                  Find the QR poster at{' '}
                  <span style={{ color: '#60A5FA' }}>
                    {clockInBlocked.locationLabel || 'the job site entrance'}
                  </span>
                  {' '}and scan it.
                </p>
                <div
                  className="w-full max-w-xs rounded-2xl p-4 mb-6 text-left space-y-3"
                  style={{ background: '#1A2433', border: '1px solid #2D3748' }}
                >
                  {[
                    'Look for the QR code poster at the site entrance',
                    'Tap "Scan QR Code" below',
                    'Point your camera at the QR code to clock in',
                  ].map((step, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span
                        className="w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ background: 'rgba(96,165,250,0.2)', color: '#60A5FA' }}
                      >
                        {i + 1}
                      </span>
                      <p className="text-sm" style={{ color: '#CBD5E1' }}>{step}</p>
                    </div>
                  ))}
                </div>

                {/* Primary action — open QR scanner directly */}
                <button
                  type="button"
                  onClick={() => {
                    setClockInBlocked(null);
                    setShowQrScanner(true);
                  }}
                  className="w-full max-w-xs py-4 rounded-2xl font-bold text-white text-lg flex items-center justify-center gap-3 active:scale-[0.98] transition-all mb-3"
                  style={{
                    background: 'linear-gradient(135deg, #1E88E5, #60A5FA)',
                    boxShadow: '0 8px 24px rgba(30,136,229,0.3)',
                  }}
                  data-testid="blocked-scan-qr-btn"
                >
                  <QrCode className="w-6 h-6" />
                  Scan QR Code
                </button>
              </>
            )}

            {/* Back button — both block types */}
            <button
              type="button"
              onClick={() => setClockInBlocked(null)}
              className="text-sm transition-colors mt-1"
              style={{ color: '#475569' }}
              data-testid="blocked-back-btn"
            >
              ← Back
            </button>
          </div>
        ) : isAlreadyClockedIn ? (
          /* Already clocked in — unchanged */
          <div className="space-y-4" data-testid="already-clocked-in">
            <div className="ht-card p-5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(0,200,150,0.15)' }}>
                <CheckCircle className="w-5 h-5" style={{ color: '#00C896' }} />
              </div>
              <div className="text-left flex-1 min-w-0">
                <p className="text-sm font-semibold" style={{ color: '#00C896' }}>Clocked In</p>
                <p className="text-xs" style={{ color: '#64748B' }}>
                  Since {(() => {
                    let iso = status.clock_in_time;
                    if (typeof iso === 'string' && !/[zZ]|[+-]\d\d:?\d\d$/.test(iso)) {
                      iso = `${iso}Z`;
                    }
                    return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
                  })()}
                </p>
                {(status.area_label || status.job_number || status.project_number) && (
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid="clock-in-tag">
                    {status.area_label && (
                      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border" style={{ background: 'rgba(0,200,150,0.12)', borderColor: 'rgba(0,200,150,0.4)', color: '#6EE7B7' }}>
                        <MapPin className="w-3 h-3" /> {status.area_label}
                      </span>
                    )}
                    {status.job_number && (
                      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border font-mono" style={{ background: 'rgba(30,136,229,0.12)', borderColor: 'rgba(30,136,229,0.4)', color: '#7DD3FC' }}>
                        <Briefcase className="w-3 h-3" /> {status.job_number}
                      </span>
                    )}
                    {status.project_number && !status.job_number && (
                      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border font-mono" style={{ background: 'rgba(168,85,247,0.12)', borderColor: 'rgba(168,85,247,0.4)', color: '#C4B5FD' }}>
                        <FolderKanban className="w-3 h-3" /> {status.project_number}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={goToDashboard}
              className="w-full py-4 rounded-2xl text-white font-semibold text-lg flex items-center justify-center gap-2 ht-btn-glow"
              style={{ background: '#1E88E5' }}
              data-testid="continue-dashboard-btn"
            >
              Continue to Dashboard <ArrowRight className="w-5 h-5" />
            </button>
            <QrPunchButton token={token} clockedIn onDone={() => { checkStatus(); }} />
            <div className="text-left">
              <BreakTracker token={token} clockedIn={true} />
            </div>
          </div>
        ) : (
          /* ── Inline selection + one Start Shift button (Aug 2026) ── */
          <div className="text-left">
            {/* Project is REQUIRED on every punch (Sep 2026) */}
            <InlineProjectRequired token={token} project={project} disabled={clockingIn} onPick={pickProject} />

            {!areasLoaded ? (
              <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" style={{ color: '#1E88E5' }} /></div>
            ) : areas.length > 0 ? (
              <InlineWhereSection
                token={token}
                areas={areas}
                selection={selection}
                projectId={project?.id}
                disabled={clockingIn}
                onSelect={pickSelection}
              />
            ) : project?.id ? (
              <InlineJobList token={token} selection={selection} projectId={project.id} disabled={clockingIn} onSelect={pickSelection} optionalHeading />
            ) : null}

            {attribError && (
              <p className="mt-3 text-sm rounded-xl px-4 py-3" style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)', color: '#FCA5A5' }} data-testid="clock-in-attrib-error">
                {attribError}
              </p>
            )}

            {/* Emergency callback tag (Aug 2026) */}
            <button
              type="button"
              onClick={() => setCallbackShift((v) => !v)}
              className="w-full mt-3 flex items-center justify-between rounded-xl px-4 transition-colors"
              style={{
                minHeight: '48px',
                background: callbackShift ? 'rgba(244,114,182,0.12)' : '#121D2E',
                border: callbackShift ? '1px solid rgba(244,114,182,0.5)' : '1px solid #2D3748',
              }}
              data-testid="callback-shift-toggle"
            >
              <span className="flex flex-col items-start text-left">
                <span className="flex items-center gap-2.5 text-sm font-medium" style={{ color: callbackShift ? '#F472B6' : '#94A3B8' }}>
                  <PhoneCall className="w-4 h-4" />
                  After-hours call-out
                </span>
                <span className="text-[11px] mt-0.5" style={{ color: '#64748B' }} data-testid="callout-policy-hint">
                  {calloutPolicy ? `${calloutPolicy.callout_window_start}–${calloutPolicy.callout_window_end} · paid at least ${calloutPolicy.my_min_hours}h` : 'Emergency or after-hours call'}
                </span>
              </span>
              <span
                className="w-10 h-6 rounded-full relative transition-colors flex-shrink-0"
                style={{ background: callbackShift ? '#F472B6' : '#2D3748' }}
              >
                <span
                  className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all"
                  style={{ left: callbackShift ? '18px' : '2px' }}
                />
              </span>
            </button>

            {/* THE one primary action */}
            <button
              onClick={startShift}
              disabled={clockingIn || !areasLoaded || !project?.id}
              className="w-full mt-4 rounded-2xl text-white font-bold text-xl flex flex-col items-center justify-center transition-all active:scale-95 disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #00C896, #00A67E)', boxShadow: '0 8px 32px rgba(0,200,150,0.3)', minHeight: '72px' }}
              data-testid="clock-in-btn"
            >
              {clockingIn ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <>
                  <span className="flex items-center gap-3"><Clock className="w-6 h-6" /> START SHIFT</span>
                  <span className="text-[11px] font-medium opacity-80 mt-0.5" data-testid="start-shift-sublabel">
                    {!project?.id ? 'Pick a project to start' : `${project.number} · ${selection.type === 'none' ? 'No area tag' : selection.label}`}
                  </span>
                </>
              )}
            </button>

            {/* Other ways to clock in */}
            <div className="mt-6">
              <p className="text-xs text-center mb-3" style={{ color: '#475569' }}>
                Other ways to clock in
              </p>
              <div className="flex gap-3 justify-center">
                {/* QR Code option */}
                <button
                  type="button"
                  onClick={() => setShowQrScanner(true)}
                  className="flex flex-col items-center gap-1.5 px-6 py-3 rounded-xl border active:scale-95 transition-all"
                  style={{ background: '#1A2433', borderColor: '#2D3748' }}
                  data-testid="qr-punch-btn"
                >
                  <QrCode className="w-5 h-5" style={{ color: '#60A5FA' }} />
                  <span className="text-xs font-medium" style={{ color: '#94A3B8' }}>Scan QR</span>
                </button>

                {/* Kiosk info option */}
                <button
                  type="button"
                  onClick={() => setShowKioskInfo(true)}
                  className="flex flex-col items-center gap-1.5 px-6 py-3 rounded-xl border active:scale-95 transition-all"
                  style={{ background: '#1A2433', borderColor: '#2D3748' }}
                  data-testid="kiosk-info-btn"
                >
                  <Monitor className="w-5 h-5" style={{ color: '#A78BFA' }} />
                  <span className="text-xs font-medium" style={{ color: '#94A3B8' }}>Kiosk</span>
                </button>
              </div>
            </div>
          </div>
        )}

        <p className="text-xs mt-4 text-center" style={{ color: '#64748B' }}>
          <MapPin className="w-3 h-3 inline mr-1" />
          GPS location will be recorded
        </p>

        {/* Shifts awaiting review & certification */}
        <div className="text-left mt-5">
          <NeedsReview token={token} />
        </div>

        {/* Payday preview — this pay period so far (Aug 2026) */}
        {payday && (payday.total_hours > 0 || payday.days_worked > 0) && (
          <button
            type="button"
            onClick={() => setPaydayOpen((v) => !v)}
            className="w-full text-left mt-5 rounded-2xl p-4"
            style={{ background: '#121D2E', border: '1px solid #2D3748' }}
            data-testid="payday-preview-card"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>
                <Wallet className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                This pay period
              </p>
              <p className="text-[10px]" style={{ color: '#475569' }}>
                {new Date(`${payday.period_start}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                {' – '}
                {new Date(`${payday.period_end}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
              </p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <p className="text-lg font-bold text-white" data-testid="payday-days">{payday.days_worked}</p>
                <p className="text-[10px]" style={{ color: '#64748B' }}>day{payday.days_worked === 1 ? '' : 's'} worked</p>
              </div>
              <div>
                <p className="text-lg font-bold font-mono" style={{ color: '#00C896' }} data-testid="payday-hours">
                  {Math.floor(payday.total_hours)}h {Math.round((payday.total_hours % 1) * 60)}m
                </p>
                <p className="text-[10px]" style={{ color: '#64748B' }}>hours{payday.active_now ? ' (on the clock)' : ''}</p>
              </div>
              <div>
                {payday.estimated_pay != null ? (
                  <>
                    <p className="text-lg font-bold font-mono" style={{ color: '#7BB8E8' }} data-testid="payday-estimate">
                      ${payday.estimated_pay.toLocaleString(undefined, { maximumFractionDigits: payday.estimated_pay < 100 ? 2 : 0 })}
                    </p>
                    <p className="text-[10px]" style={{ color: '#64748B' }}>
                      est. pay (before tax){(payday.callback_premium || 0) > 0 ? ' · incl. callback premium' : ''}
                    </p>
                  </>
                ) : (
                  <p className="text-[10px] mt-1.5" style={{ color: '#475569' }}>No rate on file</p>
                )}
              </div>
            </div>
            {paydayOpen && (payday.days || []).length > 0 && (
              <div className="mt-3 pt-3 space-y-1.5" style={{ borderTop: '1px solid #2D3748' }} data-testid="payday-day-breakdown">
                {payday.days.map((d) => (
                  <div key={d.date} className="flex items-center justify-between text-xs" data-testid={`payday-day-row-${d.date}`}>
                    <span style={{ color: '#94A3B8' }}>
                      {new Date(`${d.date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                    </span>
                    <span className="font-mono font-semibold" style={{ color: '#00C896' }}>
                      {Math.floor(d.hours)}h {Math.round((d.hours % 1) * 60)}m
                    </span>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[10px] text-center mt-2" style={{ color: '#475569' }}>
              {paydayOpen ? 'Tap to hide the day-by-day breakdown' : 'Tap to see the day-by-day breakdown'}
            </p>
          </button>
        )}

        {/* Receipt history — last few closed shifts (Aug 2026) */}
        {recentShifts.length > 0 && (
          <div className="text-left mt-5 rounded-2xl p-4" style={{ background: '#121D2E', border: '1px solid #2D3748' }} data-testid="recent-shifts-card">
            <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#64748B' }}>
              Recent shifts
            </p>
            <div className="space-y-2.5">
              {recentShifts.map((s, i) => {
                const ci = new Date(s.clock_in);
                const co = new Date(s.clock_out);
                const total = Math.round(s.hours * 60);
                return (
                  <div key={i} className="flex items-center justify-between gap-2" data-testid={`recent-shift-row-${i}`}>
                    <div className="min-w-0">
                      <p className="text-sm text-white font-medium">
                        {ci.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                      </p>
                      <p className="text-[11px]" style={{ color: '#64748B' }}>
                        {ci.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })} – {co.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                        {s.job ? ` · ${s.job}` : ''}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold font-mono" style={{ color: '#00C896' }}>
                        {Math.floor(total / 60)}h {total % 60}m
                      </p>
                      {s.meal_break_deducted && (
                        <p className="text-[10px]" style={{ color: '#FFB84D' }}>30-min break deducted</p>
                      )}
                      {s.auto_closed && (
                        <p className="text-[10px]" style={{ color: '#94A3B8' }}>auto-closed</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {(user?.role === 'admin' || user?.role === 'supervisor') && !isAlreadyClockedIn && (
          <button
            type="button"
            onClick={goToDashboard}
            className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
            style={{ color: '#7BB8E8' }}
            data-testid="skip-to-dashboard-btn"
          >
            Skip — go to Dashboard <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* QR Scanner */}
      {showQrScanner && (
        <QrScanDialog
          open={showQrScanner}
          title="Scan Site QR to Clock In"
          description="Point your camera at the QR code posted at your job site."
          skipLabel="Clock in without QR"
          onScan={async (qrToken) => {
            setShowQrScanner(false);
            try { localStorage.setItem(LAST_PUNCH_KEY, JSON.stringify(selection)); } catch { }
            await handleClockIn({
              areaLabel: selection.type === 'area' ? selection.areaLabel : null,
              jobId: selection.type === 'job' ? selection.jobId : null,
              siteQrToken: qrToken,
            });
          }}
          onSkip={() => {
            setShowQrScanner(false);
            startShift();
          }}
          onClose={() => setShowQrScanner(false)}
        />
      )}

      {/* Kiosk info bottom sheet */}
      {showKioskInfo && (
        <div
          className="fixed inset-0 z-[200] flex items-end"
          style={{ background: 'rgba(0,0,0,0.7)' }}
          onClick={() => setShowKioskInfo(false)}
          data-testid="kiosk-info-sheet"
        >
          <div
            className="w-full rounded-t-2xl p-6"
            style={{ background: '#1A2433', border: '1px solid #2D3748' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-10 h-1 rounded-full mx-auto mb-5" style={{ background: '#2D3748' }} />

            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: 'rgba(168,85,247,0.15)' }}
              >
                <Monitor className="w-5 h-5" style={{ color: '#A78BFA' }} />
              </div>
              <div>
                <p className="text-base font-semibold text-white">Kiosk Clock-In</p>
                <p className="text-xs" style={{ color: '#64748B' }}>For sites with a fixed tablet station</p>
              </div>
            </div>

            <p className="text-sm leading-relaxed mb-4 text-left" style={{ color: '#94A3B8' }}>
              If your site has a kiosk tablet set up, walk up to it and tap your name,
              then enter your 4-digit PIN to clock in or out.
            </p>

            <p className="text-xs mb-5 text-left" style={{ color: '#475569' }}>
              Don't have a PIN? Ask your supervisor or administrator to set one up for you.
            </p>

            <button
              type="button"
              onClick={async () => {
                if (badgeLoading) return;
                setBadgeLoading(true);
                try {
                  const r = await axios.get(`${API}/my/badge-qr`, { headers: { Authorization: `Bearer ${token}` } });
                  setBadge(r.data);
                  setShowKioskInfo(false);
                } catch { /* noop */ }
                finally { setBadgeLoading(false); }
              }}
              className="w-full py-3 rounded-xl font-semibold mb-2 flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
              style={{ background: 'rgba(30,136,229,0.15)', border: '1px solid rgba(30,136,229,0.5)', color: '#7BB8E8' }}
              data-testid="show-my-badge-btn"
            >
              {badgeLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <QrCode className="w-4 h-4" />}
              Show My Badge QR
            </button>
            <p className="text-[11px] mb-4 text-left" style={{ color: '#475569' }}>
              If your kiosk has Scan Badge mode, hold this QR up to the kiosk camera to punch — no PIN needed.
            </p>

            <button
              type="button"
              onClick={() => setShowKioskInfo(false)}
              className="w-full py-3 rounded-xl text-white font-semibold active:scale-[0.98] transition-all"
              style={{ background: '#1E88E5' }}
              data-testid="kiosk-info-close-btn"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Meal-break decision + clock-out receipt (QR clock-out path) */}
      <MealBreakDialog
        open={!!qrMealPrompt}
        shiftHours={qrMealPrompt?.shift_hours}
        busy={clockingIn}
        onDeduct={() => handleQrClockOut(qrMealPrompt?.tok, true, false)}
        onNoBreak={() => handleQrClockOut(qrMealPrompt?.tok, false, true)}
        onCancel={() => { setQrMealPrompt(null); setPendingQr(null); setQrJob(null); }}
      />
      <ClockOutReceipt receipt={clockOutReceipt} onClose={() => setClockOutReceipt(null)} />

      {/* My badge QR — full brightness display for kiosk scanning */}
      {badge && (
        <div
          className="fixed inset-0 z-[210] flex flex-col items-center justify-center p-6"
          style={{ background: '#FFFFFF' }}
          onClick={() => setBadge(null)}
          data-testid="my-badge-overlay"
        >
          <p className="text-lg font-bold mb-1" style={{ color: '#0B1321' }}>{badge.name || user?.name}</p>
          <p className="text-xs mb-5" style={{ color: '#475569' }}>Hold this up to the kiosk camera to punch in or out</p>
          <img
            src={badge.qr_png}
            alt="My badge QR"
            className="w-72 h-72 max-w-full"
            data-testid="my-badge-qr-img"
          />
          <button
            type="button"
            onClick={() => setBadge(null)}
            className="mt-8 px-8 py-3 rounded-xl font-semibold text-white"
            style={{ background: '#1E88E5' }}
            data-testid="my-badge-close-btn"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Inline "Where are you working?" — replaces the old area picker modal.
// Big 48px+ chips, GPS-suggested area first (preselected), plus an inline
// "Specific job" search. Tapping selects; only Start Shift clocks in.
// ─────────────────────────────────────────────────────────────────────────
function InlineWhereSection({ token, areas, selection, projectId, disabled, onSelect }) {
  const [mode, setMode] = useState('areas'); // 'areas' | 'job'
  const [showAll, setShowAll] = useState(false);

  const ordered = useMemo(() => [...areas].sort((a, b) => {
    if (a?.suggested && !b?.suggested) return -1;
    if (!a?.suggested && b?.suggested) return 1;
    const da = a?.distance_km;
    const db = b?.distance_km;
    if (da != null && db != null) return da - db;
    if (da != null) return -1;
    if (db != null) return 1;
    return 0;
  }), [areas]);

  const visible = showAll ? ordered : ordered.slice(0, 4);

  return (
    <div data-testid="inline-where-section" className="mt-4">
      <p className="text-[11px] font-bold uppercase tracking-widest mb-2" style={{ color: '#64748B' }}>
        Where are you working? <span className="normal-case tracking-normal font-medium">(optional tag)</span>
      </p>

      {/* Segmented toggle */}
      <div className="flex gap-2 mb-2.5" role="tablist">
        <button
          type="button"
          onClick={() => setMode('areas')}
          className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-semibold transition ${
            mode === 'areas' ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/50' : 'text-slate-400 border border-[#2D3748]'
          }`}
          style={{ minHeight: '48px' }}
          data-testid="inline-tab-areas"
          role="tab"
          aria-selected={mode === 'areas'}
        >
          <MapIcon className="w-4 h-4 inline mr-1.5" /> Work Area
        </button>
        <button
          type="button"
          onClick={() => setMode('job')}
          className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-semibold transition ${
            mode === 'job' ? 'bg-blue-500/15 text-blue-300 border border-blue-500/50' : 'text-slate-400 border border-[#2D3748]'
          }`}
          style={{ minHeight: '48px' }}
          data-testid="inline-tab-job"
          role="tab"
          aria-selected={mode === 'job'}
        >
          <Briefcase className="w-4 h-4 inline mr-1.5" /> Specific Job
        </button>
      </div>

      {mode === 'areas' ? (
        <div className="space-y-2" data-testid="inline-area-list">
          {visible.map((area) => {
            const name = area?.name || (typeof area === 'string' ? area : '');
            const slug = name.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
            const isSuggested = !!area?.suggested;
            const isSelected = selection.type === 'area' && selection.areaLabel === name;
            const dist = area?.distance_km;
            return (
              <button
                type="button"
                key={name}
                onClick={() => !disabled && onSelect({ type: 'area', label: name, areaLabel: name })}
                disabled={disabled}
                className={`w-full px-4 rounded-xl text-left text-white font-medium flex items-center justify-between transition active:scale-[0.98] disabled:opacity-50 ${
                  isSelected ? 'ring-2 ring-emerald-400' : ''
                }`}
                style={{
                  minHeight: '52px',
                  background: isSelected
                    ? 'linear-gradient(135deg, rgba(0,200,150,0.3), rgba(0,166,126,0.2))'
                    : 'linear-gradient(135deg, rgba(0,200,150,0.1), rgba(0,166,126,0.05))',
                  border: isSelected ? '1px solid rgba(0,200,150,0.9)' : '1px solid rgba(0,200,150,0.35)',
                }}
                data-testid={`area-chip-${slug}`}
                aria-pressed={isSelected}
              >
                <span className="flex items-center gap-3 min-w-0">
                  {isSelected
                    ? <CheckCircle className="w-5 h-5 flex-shrink-0 text-emerald-300" />
                    : <MapPin className="w-4 h-4 flex-shrink-0 text-emerald-400" />}
                  <span className="truncate">{name}</span>
                  {isSuggested && (
                    <span
                      className="ml-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/30 text-emerald-100 border border-emerald-300/40 flex-shrink-0"
                      data-testid={`area-chip-suggested-${slug}`}
                    >
                      📍 Near you
                    </span>
                  )}
                </span>
                {dist != null && (
                  <span className="text-[11px] text-slate-300 flex-shrink-0 ml-2">
                    {dist < 1 ? `${Math.round(dist * 1000)}m` : `${dist.toFixed(dist < 10 ? 1 : 0)}km`}
                  </span>
                )}
              </button>
            );
          })}
          {!showAll && ordered.length > 4 && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="w-full py-2.5 rounded-xl text-sm font-medium text-slate-400 hover:text-white border border-[#2D3748]"
              style={{ minHeight: '48px' }}
              data-testid="show-all-areas-btn"
            >
              Show all {ordered.length} areas
            </button>
          )}
          {/* No-area escape hatch as a selectable option */}
          <button
            type="button"
            onClick={() => !disabled && onSelect({ type: 'none', label: null })}
            disabled={disabled}
            className={`w-full px-4 rounded-xl text-left text-sm font-medium flex items-center gap-3 transition disabled:opacity-50 ${
              selection.type === 'none' ? 'text-white ring-2 ring-slate-400' : 'text-slate-400'
            }`}
            style={{ minHeight: '48px', border: '1px solid #2D3748', background: selection.type === 'none' ? 'rgba(160,174,192,0.08)' : 'transparent' }}
            data-testid="area-picker-skip"
            aria-pressed={selection.type === 'none'}
          >
            {selection.type === 'none' ? <CheckCircle className="w-4 h-4 text-slate-300" /> : <X className="w-4 h-4" />}
            No area tag
          </button>
        </div>
      ) : (
        <InlineJobList token={token} selection={selection} projectId={projectId} disabled={disabled} onSelect={onSelect} />
      )}
    </div>
  );
}

// Inline job search — same fetch/rank logic as the old modal's job tab.
// Filtered to the selected project (jobs without a project are always offered).
function InlineJobList({ token, selection, projectId, disabled, onSelect, optionalHeading = false }) {
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [jobSearch, setJobSearch] = useState('');

  useEffect(() => {
    axios
      .get(`${API}/jobs`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : r.data?.jobs || [];
        const rank = { in_progress: 0, paused: 1, todo: 2, pending: 2 };
        const filtered = list
          .filter((j) => !j.deleted_at && !j.archived && (rank[j.status] !== undefined))
          .sort((a, b) => (rank[a.status] ?? 9) - (rank[b.status] ?? 9));
        setJobs(filtered);
      })
      .catch(() => setJobs([]))
      .finally(() => setJobsLoading(false));
  }, [token]);

  const filteredJobs = useMemo(() => {
    const q = jobSearch.trim().toLowerCase();
    const scoped = projectId ? jobs.filter((j) => j.project_id === projectId || !j.project_id) : jobs;
    if (!q) return scoped.slice(0, 40);
    return scoped.filter((j) => [j.title, j.job_number, j.customer_name, j.city]
      .some((v) => (v || '').toLowerCase().includes(q))).slice(0, 40);
  }, [jobs, jobSearch, projectId]);

  if (optionalHeading && !jobsLoading && jobs.length === 0) return null;

  return (
    <div className="space-y-2 mt-4" data-testid="inline-job-list">
      {optionalHeading && (
        <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: '#64748B' }}>
          Job under this project <span className="normal-case tracking-normal font-medium">(optional)</span>
        </p>
      )}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          value={jobSearch}
          onChange={(e) => setJobSearch(e.target.value)}
          placeholder="Search jobs by number, title, customer…"
          className="w-full pl-9 pr-3 rounded-xl bg-[#0F1923] border border-[#2D3748] text-white text-sm focus:outline-none focus:border-blue-500"
          style={{ minHeight: '48px' }}
          data-testid="area-picker-job-search"
        />
      </div>
      <div className="space-y-1.5 max-h-60 overflow-y-auto">
        {jobsLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
          </div>
        ) : filteredJobs.length === 0 ? (
          <p className="text-xs text-slate-500 italic py-3 text-center">
            {jobs.length === 0 ? 'No active jobs assigned right now.' : 'No matches — try a different search.'}
          </p>
        ) : (
          filteredJobs.map((j) => {
            const isSelected = selection.type === 'job' && selection.jobId === j.id;
            return (
              <button
                type="button"
                key={j.id}
                onClick={() => !disabled && onSelect({ type: 'job', label: j.job_number ? `${j.job_number} — ${j.title}` : j.title, jobId: j.id, jobProjectId: j.project_id || null, jobProjectNumber: j.project_number || null, jobProjectName: j.project_name || null })}
                disabled={disabled}
                className={`w-full px-3 py-2.5 rounded-xl text-left flex items-start gap-2 border disabled:opacity-50 transition ${
                  isSelected ? 'border-blue-400 bg-blue-500/15 ring-1 ring-blue-400' : 'border-[#2D3748] hover:bg-blue-500/10 hover:border-blue-500/40'
                }`}
                style={{ minHeight: '48px' }}
                data-testid={`area-picker-job-${j.id}`}
                aria-pressed={isSelected}
              >
                {j.job_number && (
                  <span className="text-[10px] font-mono text-blue-300 bg-blue-500/10 px-1.5 py-0.5 rounded mt-0.5 flex-shrink-0">
                    {j.job_number}
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-white truncate">{j.title}</p>
                  <p className="text-[11px] text-slate-400 truncate">
                    {j.customer_name}{j.status ? ` · ${String(j.status).replace(/_/g, ' ')}` : ''}
                  </p>
                </div>
                {isSelected && <CheckCircle className="w-4 h-4 text-blue-300 flex-shrink-0 mt-1" />}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Project picker — REQUIRED on every punch (Sep 2026). "General Work" is
// always pinned first for genuine non-project time.
// ─────────────────────────────────────────────────────────────────────────
function InlineProjectRequired({ token, project, disabled, onPick }) {
  const [expanded, setExpanded] = useState(!project?.id);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [general, setGeneral] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/company/general-work-project`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        if (data?.selectable === false) {
          setGeneral(null);
          if (project?.number === 'GENERAL') { onPick(null); setExpanded(true); } // company now requires a real project
        } else setGeneral(data);
      }).catch(() => setGeneral(null));
  }, [token]);

  useEffect(() => {
    if (!expanded) return;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/projects/search?q=${encodeURIComponent(query || '')}&limit=30`, { headers: { Authorization: `Bearer ${token}` } });
        setResults(Array.isArray(data) ? data.filter((p) => !p.is_system) : []);
      } catch { setResults([]); } finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [query, token, expanded]);

  const pick = (p) => { onPick({ id: p.id, number: p.project_number, name: p.name }); setExpanded(false); setQuery(''); };
  const showGeneral = general && (!query.trim() || `${general.project_number} ${general.name}`.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <div data-testid="inline-project-required">
      <p className="text-[11px] font-bold uppercase tracking-widest mb-2" style={{ color: '#64748B' }}>
        Which project? <span className="normal-case tracking-normal font-medium" style={{ color: '#F87171' }}>(required)</span>
      </p>
      {project?.id && !expanded ? (
        <div className="flex items-center justify-between px-4 rounded-xl" style={{ minHeight: '52px', background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.45)' }} data-testid="selected-project-chip">
          <span className="flex items-center gap-2 min-w-0">
            <FolderKanban className="w-4 h-4 flex-shrink-0" style={{ color: '#10B981' }} />
            <span className="text-sm text-white font-medium truncate">{project.number} — {project.name}</span>
          </span>
          <button type="button" onClick={() => setExpanded(true)} disabled={disabled} className="text-xs font-semibold px-2.5 py-1.5 rounded-lg hover:bg-white/10" style={{ color: '#A0AEC0' }} data-testid="change-project-btn">
            Change
          </button>
        </div>
      ) : (
        <div className="rounded-xl p-3" style={{ background: '#111C2C', border: '1px solid #2D3748' }}>
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#64748B' }} />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by project # or name…"
              className="w-full pl-9 pr-3 rounded-xl text-sm" style={{ minHeight: '48px', background: '#0F1923', color: 'white', border: '1px solid #2D3748' }}
              data-testid="picker-search-input" />
          </div>
          <div className="rounded-xl overflow-y-auto max-h-56" style={{ background: '#0F1923', border: '1px solid #2D3748' }} data-testid="picker-results">
            {showGeneral && (
              <button type="button" onClick={() => pick(general)} disabled={disabled}
                className="w-full px-3 py-2.5 text-left hover:bg-white/5 border-b transition-colors flex items-center justify-between"
                style={{ borderColor: '#2D3748', minHeight: '48px' }} data-testid="picker-pick-general-work">
                <span>
                  <div className="font-mono text-xs" style={{ color: '#94A3B8' }}>{general.project_number}</div>
                  <div className="text-sm text-white">{general.name}</div>
                </span>
                <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(148,163,184,0.15)', color: '#CBD5E1' }}>non-project time</span>
              </button>
            )}
            {loading && <div className="p-4 text-center text-xs" style={{ color: '#A0AEC0' }}>Searching…</div>}
            {!loading && results.length === 0 && !showGeneral && (
              <div className="p-4 text-center text-xs" style={{ color: '#A0AEC0' }}>No matching projects.</div>
            )}
            {!loading && results.map((p) => (
              <button key={p.id} type="button" onClick={() => pick(p)} disabled={disabled}
                className="w-full px-3 py-2.5 text-left hover:bg-white/5 border-b last:border-b-0 transition-colors"
                style={{ borderColor: '#2D3748', minHeight: '48px' }} data-testid={`picker-pick-${p.id}`}>
                <div className="font-mono text-xs" style={{ color: '#10B981' }}>{p.project_number}</div>
                <div className="text-sm text-white">{p.name}</div>
                {p.customer_name && <div className="text-[11px]" style={{ color: '#A0AEC0' }}>{p.customer_name}</div>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
